import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Switch } from 'antd';
import {
  ActionItem,
  ActionLabel,
  Container,
  Dropdown,
  Tooltip
} from './styled';
import RadioGroup from '../../../../components/RadioGroup';
import RangeDatePicker from '../../../../components/RangeDatePicker';
import SortComponent from '../../../../components/SortComponent';
import FilterComponent from '../../../../components/FilterComponent';

import { clone, cloneDeep, isEmpty, times } from '../../../../util/lodashlike';
const defaultProps = {
  defaultDateRange: []
};

const DEFAULT_STATE = {
  selectedDays: 30,
  selectedDateRange: [moment().subtract(30, 'days').add(1, 'days'), moment()],
  selectedTypology: {
    id: 'all',
    name: 'All'
  },
  isTriggeredOnly: true
};

class WidgetActionComponent extends React.Component {
  // Default Dates
  isDateAvailable = this.props.defaultDateRange.length;
  state = {
    ...DEFAULT_STATE,
    selectedDateRange: this.isDateAvailable
      ? this.props.defaultDateRange
      : DEFAULT_STATE.selectedDateRange
  };

  onDateRangeChange = () => {
    const {
      selectedDateRange,
      selectedTypology,
      isTriggeredOnly,
      sortObject,
      selectedFilters
    } = this.state;
    const { onChangeActions, hasTriggeredOnlySwitch, hasTypologiesFilter } =
      this.props;

    let q = {};
    q['sort'] = sortObject || {};
    q['filter'] = selectedFilters;
    const from = selectedDateRange[0];
    const to = selectedDateRange[1];

    if (onChangeActions) {
      onChangeActions({
        from,
        to,
        q,
        typologyId: hasTypologiesFilter
          ? selectedTypology.id === 'all'
            ? undefined
            : selectedTypology.id
          : undefined,
        isTriggeredOnly: hasTriggeredOnlySwitch ? isTriggeredOnly : undefined
      });
    }
  };

  onRadioGroupChange = (days) => {
    let currentDate = moment();
    let currentDateMinusDays = moment().subtract(days, 'days').add(1, 'days');

    // If Default date available apply default dates
    if (this.isDateAvailable) {
      const [, toDate] = this.props.defaultDateRange;
      currentDate = toDate;
      currentDateMinusDays = cloneDeep(toDate)
        .subtract(days, 'days')
        .add(1, 'days');
    }

    this.setState(
      {
        selectedDateRange: [currentDateMinusDays, currentDate],
        selectedDays: days
      },
      () => {
        this.onDateRangeChange();
      }
    );
  };

  onDatePickerChange = (dates) => {
    if (dates) {
      this.setState(
        {
          selectedDateRange: dates,
          selectedDays: null
        },
        () => {
          this.onDateRangeChange();
        }
      );
    }
  };

  onTypologyChange = (value) => {
    this.setState(
      {
        isTriggeredOnly: true,
        selectedTypology: value
      },
      () => {
        this.onDateRangeChange();
      }
    );
  };

  onChangeTriggeredOnly = (value) => {
    const { selectedTypology } = this.state;
    this.setState(
      {
        isTriggeredOnly: value,
        selectedTypology: value
          ? isEmpty(selectedTypology.name)
            ? DEFAULT_STATE.selectedTypology
            : selectedTypology
          : {
              id: 'all',
              name: ''
            }
      },
      () => {
        this.onDateRangeChange();
      }
    );
  };

  onSortChange = (sortObject) => {
    this.setState({ sortObject });
  };

  onFilterChange = (filterItems) => {
    this.setState({ selectedFilters: filterItems });
  };

  // Date Range Limit
  disableFutureDate = (current) => {
    if (current) {
      return this.isDateAvailable
        ? current > this.props.defaultDateRange[1].endOf('day')
        : current > moment().endOf('day');
    }
  };

  render() {
    const {
      selectedDateRange,
      selectedDays,
      selectedTypology,
      isTriggeredOnly
    } = this.state;
    const {
      hasTriggeredOnlySwitch,
      hasDatePicker,
      hasDurationPicker,
      hasTypologiesFilter,
      hasFilterPicker,
      hasSortPicker,
      sortItems,
      filterItems,
      typologies = [],
      className,
      showLabels = true
    } = this.props;
    const durationItems = [
      { label: '7 Days', value: 7 },
      { label: '30 Days', value: 30 },
      { label: '90 Days', value: 90 },
      { label: '180 Days', value: 180 },
      { label: '360 Days', value: 360 }
    ];
    const typologiesOptions = [
      {
        id: 'all',
        name: isTriggeredOnly ? 'All' : ''
      },
      ...typologies.map((typology) => ({
        id: typology.id,
        name: typology.name
      }))
    ];

    return (
      <Container className={className}>
        {hasTriggeredOnlySwitch && (
          <ActionItem>
            {showLabels && <ActionLabel>Triggered Only</ActionLabel>}
            <Switch
              checked={isTriggeredOnly}
              onChange={this.onChangeTriggeredOnly}
            />
          </ActionItem>
        )}

        {hasDurationPicker ? (
          <ActionItem>
            {showLabels && <ActionLabel>Duration</ActionLabel>}
            <RadioGroup
              items={durationItems}
              defaultValue={selectedDays}
              onChange={this.onRadioGroupChange}
              size="small"
            />
          </ActionItem>
        ) : null}

        {hasDatePicker ? (
          <ActionItem>
            {showLabels && <ActionLabel>Date Range</ActionLabel>}
            <RangeDatePicker
              defaultDates={selectedDateRange}
              onChange={this.onDatePickerChange}
              size="small"
              disabledDate={this.disableFutureDate}
            />
          </ActionItem>
        ) : null}

        {hasSortPicker ? (
          <ActionItem>
            {showLabels && <ActionLabel>Sort</ActionLabel>}
            <SortComponent
              columnGroups={sortItems}
              onChange={this.onSortChange}
            />
          </ActionItem>
        ) : null}

        {hasFilterPicker ? (
          <ActionItem>
            {showLabels && <ActionLabel>Filter</ActionLabel>}
            <FilterComponent
              columnNames={filterItems}
              onFilterChange={this.onFilterChange}
            />
          </ActionItem>
        ) : null}

        {hasTypologiesFilter ? (
          <ActionItem>
            {showLabels && <ActionLabel>Typologies</ActionLabel>}
            <Dropdown
              disabled={!isTriggeredOnly}
              dropdownValue={typologiesOptions.find(
                (option) => option.id === selectedTypology.id
              )}
              dropdownOptions={typologiesOptions?.map((option) => ({
                ...option,
                name: <Tooltip content={option.name}>{option.name}</Tooltip>
              }))}
              handleValueChange={this.onTypologyChange}
            />
          </ActionItem>
        ) : null}
      </Container>
    );
  }
}

WidgetActionComponent.defaultProps = defaultProps;

WidgetActionComponent.propTypes = {
  className: PropTypes.string,
  size: PropTypes.string,

  radioGroupItems: PropTypes.array,
  defaultDates: PropTypes.array,
  defaultDays: PropTypes.number,
  sortItems: PropTypes.array,
  filterItems: PropTypes.array,

  hasTriggeredOnlySwitch: PropTypes.bool,
  hasDurationPicker: PropTypes.bool,
  hasDatePicker: PropTypes.bool,
  hasSortPicker: PropTypes.bool,
  hasFilterPicker: PropTypes.bool,
  hasTypologiesFilter: PropTypes.bool,
  typologies: PropTypes.array,

  onChangeActions: PropTypes.func,
  showLabels: PropTypes.bool,

  defaultDateRange: PropTypes.array
};

export default WidgetActionComponent;
